import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { CssBaseline, PaletteMode } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { getTheme } from "./index";

// What the user explicitly chose. "system" follows the OS preference.
export type ColorModePreference = "light" | "dark" | "system";

type ColorModeContextValue = {
  // The resolved mode actually in use (never "system").
  mode: PaletteMode;
  // The user's stored choice.
  preference: ColorModePreference;
  setPreference: (preference: ColorModePreference) => void;
  // Kept for backwards-compat: flips between explicit light and dark.
  toggleColorMode: () => void;
};

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: "light",
  preference: "system",
  setPreference: () => {},
  toggleColorMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

const STORAGE_KEY = "dz-color-mode";

function getSystemMode(): PaletteMode {
  try {
    if (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    ) {
      return "dark";
    }
  } catch {
    // matchMedia unavailable — fall through to light.
  }
  return "light";
}

function getInitialPreference(): ColorModePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // "light"/"dark" covers users who saved a choice before "system" existed.
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage unavailable — fall through to system default.
  }
  return "system";
}

export const ColorModeProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [preference, setPreference] = useState<ColorModePreference>(
    getInitialPreference
  );
  const [systemMode, setSystemMode] = useState<PaletteMode>(getSystemMode);

  // Track the OS preference so "system" updates live when the user changes it.
  useEffect(() => {
    if (!window.matchMedia) {
      return;
    }
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (event: MediaQueryListEvent) =>
      setSystemMode(event.matches ? "dark" : "light");
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // ignore persistence errors (e.g. private mode)
    }
  }, [preference]);

  const mode: PaletteMode = preference === "system" ? systemMode : preference;

  // Expose the resolved mode to plain CSS (the Amplify Authenticator is styled
  // with CSS variables, not MUI) and let the browser theme native controls.
  // Layout effect so the attribute lands before first paint — no light flash.
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-dz-mode", mode);
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      preference,
      setPreference,
      toggleColorMode: () =>
        setPreference(mode === "light" ? "dark" : "light"),
    }),
    [mode, preference]
  );

  const theme = useMemo(() => getTheme(mode), [mode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
